import moment from 'moment-timezone';
import { KIND_OF_REPORTS } from '../../constants';
import { DateUtil } from '@src/packages/utils/dates';

/**
 * Parse Spanish date format (DD/MM/YYYY) to Date object
 * @param {string} dateStr - Date string in DD/MM/YYYY format
 * @returns {Date} Parsed date or epoch date if invalid
 */
const parseSpanishDate = dateStr => {
  if (!dateStr || dateStr === '-') return new Date(0);
  const parsedDate = moment(dateStr, DateUtil.DEFAULT_DATE_FORMAT, true);
  return parsedDate.isValid() ? parsedDate.toDate() : new Date(0);
};

/**
 * Parse date string that may include time info (used for Issues report)
 * Format: DD/MM/YYYY - HH:mm or just DD/MM/YYYY
 * @param {string} dateStr - Date string
 * @returns {Date} Parsed date or epoch date if invalid
 */
const parseIssueDate = dateStr => {
  if (!dateStr || dateStr === '-') return new Date(0);
  // Extract date part before the ' - ' (time separator)
  const datePart = dateStr.split(' - ')[0];
  return parseSpanishDate(datePart);
};

/**
 * Sort function for comparing two records by a date field
 * @param {Object} a - First record
 * @param {Object} b - Second record
 * @param {string} dateField - Name of the date field to compare
 * @param {Function} parser - Optional custom date parser function
 * @returns {number} Sort comparison result
 */
const sortByDateField = (a, b, dateField, parser = parseSpanishDate) => {
  const dateA = parser(a[dateField]);
  const dateB = parser(b[dateField]);
  return dateA - dateB; // Ascending order
};

/**
 * Merges report data from pagination/infinite scroll
 * Handles different report structures: flat (Attendance, Holidays) and nested (Daily, Modifications, Issues)
 *
 * @param {Object} prevData - Previous accumulated report data
 * @param {Object} newData - New data to merge
 * @param {string} reportType - Type of report (from KIND_OF_REPORTS constant)
 * @returns {Object} Merged report data
 */
export const mergeReportData = (prevData = {}, newData, reportType) => {
  const mergedData = { ...prevData };
  const isAttendance = reportType === KIND_OF_REPORTS.ATTENDANCE;
  const isHolidaysFlat = reportType === KIND_OF_REPORTS.HOLIDAYS;

  if (isAttendance) {
    Object.keys(newData).forEach(empleadoId => {
      if (mergedData[empleadoId]) {
        const prevEmployee = mergedData[empleadoId];
        const newEmployee = newData[empleadoId];
        const combinedSource = [...(prevEmployee.source || []), ...(newEmployee.source || [])];
        combinedSource.sort((a, b) => sortByDateField(a, b, 'date'));

        mergedData[empleadoId] = {
          ...prevEmployee,
          source: combinedSource,
          columns: prevEmployee.columns,
        };
      } else {
        mergedData[empleadoId] = newData[empleadoId];
      }
    });
  } else if (isHolidaysFlat) {
    Object.keys(newData).forEach(empleadoId => {
      if (mergedData[empleadoId]) {
        const prevEmployee = mergedData[empleadoId];
        const newEmployee = newData[empleadoId];
        const combinedSource = [...(prevEmployee.source || []), ...(newEmployee.source || [])];
        combinedSource.sort((a, b) => sortByDateField(a, b, 'date'));

        mergedData[empleadoId] = {
          ...newEmployee,
          employee: newEmployee.employee?.length > 0 ? newEmployee.employee : prevEmployee.employee,
          employer: newEmployee.employer?.length > 0 ? newEmployee.employer : prevEmployee.employer,
          source: combinedSource,
        };
      } else {
        mergedData[empleadoId] = newData[empleadoId];
      }
    });
  } else {
    Object.keys(newData).forEach(empleadoId => {
      if (mergedData[empleadoId]) {
        Object.keys(newData[empleadoId]).forEach(week => {
          if (mergedData[empleadoId][week]) {
            let combinedRecords = [...mergedData[empleadoId][week], ...newData[empleadoId][week]];

            if (reportType === KIND_OF_REPORTS.ATTENDANCE || reportType === KIND_OF_REPORTS.DAILY) {
              combinedRecords.sort((a, b) => sortByDateField(a, b, 'date'));
            } else if (reportType === KIND_OF_REPORTS.MODIFICATIONS) {
              combinedRecords.sort((a, b) => sortByDateField(a, b, 'assignDate'));
            } else if (reportType === KIND_OF_REPORTS.ISSUES) {
              combinedRecords.sort((a, b) => sortByDateField(a, b, 'start', parseIssueDate));
            }

            mergedData[empleadoId][week] = combinedRecords;
          } else {
            mergedData[empleadoId][week] = newData[empleadoId][week];
          }
        });
      } else {
        mergedData[empleadoId] = newData[empleadoId];
      }
    });
  }

  return mergedData;
};
